import React, { useEffect, useState, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, Button, Empty, Modal, Form, Input, Select, Row, Col, Typography, Space, Dropdown, ConfigProvider, Segmented, message } from 'antd';
import { Trash2, MoreVertical, Film, Zap, Copy, LayoutGrid, List, ChevronDown, Wand2, Plus, Sparkles, FileSliders, Presentation } from 'lucide-react';
import { projectsManager, Project, CreateProjectData } from '../../services/projectsManager';
import { TemplateGallery, TemplateCustomizer, TemplatePreviewModal } from '../templates';
import { FeatureAnnouncementModal } from '../modals/FeatureAnnouncementModal';
import { SmartTemplateWizard, MakeItYoursWizard } from '../templates/wizards';
import { Template } from '../../../../types/template';
import { QuickActionCard } from './QuickActionCard';
import { TemplateCarousel } from './TemplateCarousel';
import { OnboardingChecklist } from '../OnboardingChecklist';
import { ProjectCreationOverlay } from './ProjectCreationOverlay';
import { PresentationImportModal } from '../PresentationImport';
import { initMakeItYoursAtom } from '../../atoms/wizardAtoms';
import { useSetAtom } from 'jotai';
import './ProjectsDashboard.scss';
import type { MenuProps } from 'antd';

const { Title, Text, Paragraph } = Typography;
const { Option } = Select;

// Helper to get platform-friendly labels
const getAspectRatioLabel = (aspectRatio: string) => {
  const ratios: Record<string, { label: string }> = {
    '16:9': { label: 'Landscape' },
    '9:16': { label: 'Portrait' },
    '1:1': { label: 'Square' },
    '4:5': { label: 'Portrait' },
  };
  return ratios[aspectRatio] || { label: aspectRatio };
};

type ViewMode = 'grid' | 'list';

export const ProjectsDashboard: React.FC = () => {
  const navigate = useNavigate();
  const [projects, setProjects] = useState<Project[]>([]);
  const [loading, setLoading] = useState(true);
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [isTemplateGalleryVisible, setIsTemplateGalleryVisible] = useState(false);
  const [hideTemplateGalleryModal, setHideTemplateGalleryModal] = useState(false);
  const [showSmartWizard, setShowSmartWizard] = useState(false);
  const [showPresentationImport, setShowPresentationImport] = useState(false);
  const [showMakeItYoursWizard, setShowMakeItYoursWizard] = useState(false);
  const [showTemplateCustomizer, setShowTemplateCustomizer] = useState(false);
  const [viewMode, setViewMode] = useState<ViewMode>('grid');
  const [selectedTemplateForWizard, setSelectedTemplateForWizard] = useState<Template | null>(null);
  const [previewTemplate, setPreviewTemplate] = useState<Template | null>(null);
  const [displayCount, setDisplayCount] = useState(8);
  const [loadingMore, setLoadingMore] = useState(false);
  const [creatingProject, setCreatingProject] = useState(false);
  const loadMoreRef = useRef<HTMLDivElement>(null);
  const [form] = Form.useForm();
  const initMakeItYours = useSetAtom(initMakeItYoursAtom);

  const loadMoreProjects = useCallback(() => {
    if (loadingMore || displayCount >= projects.length) return;
    setLoadingMore(true);
    setTimeout(() => {
      setDisplayCount(prev => Math.min(prev + 8, projects.length));
      setLoadingMore(false);
    }, 300);
  }, [loadingMore, displayCount, projects.length]);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting && !loadingMore && displayCount < projects.length) {
          loadMoreProjects();
        }
      },
      { threshold: 0.1 }
    );

    if (loadMoreRef.current) {
      observer.observe(loadMoreRef.current);
    }

    return () => observer.disconnect();
  }, [loadMoreProjects, loadingMore, displayCount, projects.length]);

  useEffect(() => {
    setDisplayCount(8);
  }, [projects]);

  const handleStartBlank = () => {
    navigate('/recorder?mode=blank');
  };

  const newProjectMenuItems: MenuProps['items'] = [
    {
      key: 'template',
      icon: <Film size={16} />,
      label: 'Browse Templates',
      onClick: () => setIsTemplateGalleryVisible(true),
    },
    {
      key: 'smart',
      icon: <Wand2 size={16} />,
      label: 'Help Me Choose',
      onClick: () => setShowSmartWizard(true),
    },
    {
      type: 'divider',
    },
    {
      key: 'blank',
      icon: <Plus size={16} />,
      label: 'Start Blank',
      onClick: () => setShowTemplateCustomizer(true),
    },
  ];

  const handleHideTemplateGallery = (visible: boolean) => {
    setHideTemplateGalleryModal(!visible);
  };

  useEffect(() => {
    loadProjects();
  }, []);

  const loadProjects = async () => {
    try {
      setLoading(true);
      // Server extracts userId from JWT token for filtering
      const data = await projectsManager.getProjects();
      setProjects(Array.isArray(data) ? data : []);
    } catch (error) {
      console.error('Failed to load projects:', error);
      setProjects([]);
    } finally {
      setLoading(false);
    }
  };

  const handleCreateProject = async (values: CreateProjectData) => {
    try {
      const newProject = await projectsManager.createProject(values);
      setIsModalVisible(false);
      form.resetFields();
      navigate(`/recorder?mode=project&projectId=${newProject.id}`);
    } catch (error) {
      console.error('Failed to create project:', error);
    }
  };

  const handleDuplicateProject = async (projectId: number, e?: any) => {
    if (e) {
      e.stopPropagation();
      e.domEvent?.stopPropagation();
    }
    
    try {
      const project = projects.find(p => p.id === projectId);
      if (!project) return;

      const newProject = await projectsManager.createProject({
        name: `${project.name} (Copy)`,
        description: project.description,
        aspectRatio: project.aspectRatio,
      });

      try {
        const scenes = await projectsManager.getScenes(projectId);
        const sceneIdMap: Record<number, number> = {};
        
        for (const scene of scenes) {
          try {
            const newScene = await projectsManager.createScene({
              projectId: newProject.id,
              title: scene.title,
              description: scene.description,
              videoUrl: undefined,
              duration: scene.duration || 0,
              sceneOrder: scene.sceneOrder,
              metadata: scene.metadata,
            });
            sceneIdMap[scene.id] = newScene.id;
          } catch (sceneError) {
            console.error('Error duplicating scene:', sceneError);
          }
        }

        try {
          const transitions = await projectsManager.getTransitions(projectId);
          for (const transition of transitions) {
            const newFromSceneId = sceneIdMap[transition.fromSceneId];
            const newToSceneId = sceneIdMap[transition.toSceneId];
            
            if (newFromSceneId && newToSceneId) {
              try {
                await projectsManager.createTransition({
                  projectId: newProject.id,
                  fromSceneId: newFromSceneId,
                  toSceneId: newToSceneId,
                  type: transition.type,
                  duration: transition.duration,
                  sortOrder: transition.sortOrder,
                });
              } catch (transitionError) {
                console.error('Error duplicating transition:', transitionError);
              }
            }
          }
        } catch (transitionsError) {
          console.error('Error fetching transitions:', transitionsError);
        }
      } catch (scenesError) {
        console.error('Error fetching scenes:', scenesError);
      }

      await loadProjects();
      
      Modal.success({
        title: 'Project Duplicated',
        content: `"${project.name}" has been duplicated successfully!`,
        okButtonProps: {
          style: { backgroundColor: '#4A3CFA', borderColor: '#4A3CFA' }
        }
      });
    } catch (error) {
      console.error('Failed to duplicate project:', error);
      Modal.error({
        title: 'Duplication Failed',
        content: 'There was an error duplicating the project. Please try again.',
        okButtonProps: {
          style: { backgroundColor: '#4A3CFA', borderColor: '#4A3CFA' }
        }
      });
    }
  };

  const handleDeleteProject = async (projectId: number, e?: any) => {
    if (e) {
      e.stopPropagation();
      e.domEvent?.stopPropagation();
    }
    Modal.confirm({
      title: 'Delete Project',
      content: 'Are you sure you want to delete this project? This action cannot be undone.',
      okText: 'Delete',
      okType: 'danger',
      onOk: async () => {
        try {
          await projectsManager.deleteProject(projectId);
          loadProjects();
        } catch (error) {
          console.error('Failed to delete project:', error);
        }
      },
    });
  };

  const handleOpenProject = (projectId: number) => {
    navigate(`/recorder?mode=project&projectId=${projectId}`);
  };

  const handleTemplateSelect = async (template: Template) => {
    setCreatingProject(true);
    try {
      // Use template's aspect ratio if defined, fallback to 16:9
      const aspectRatio = template.aspectRatio || '16:9';
      
      const newProject = await projectsManager.createProject({
        name: template.title,
        description: template.description,
        aspectRatio,
        templateId: template.id,
      });

      const sceneDataList = template.scenes.map((scene, index) => {
        // Use template's backgroundImage for all scenes if available, otherwise use scene's defined background
        const sceneBackground = template.backgroundImage
          ? { type: 'image' as const, imageUrl: template.backgroundImage }
          : scene.background;
        
        return {
          projectId: newProject.id,
          title: scene.title,
          description: scene.notes,
          videoUrl: undefined,
          duration: scene.suggestedDuration * 1000,
          sceneOrder: index,
          metadata: {
            script: template.scripts.find(s => s.key === scene.scriptKey)?.content,
            tags: scene.tags,
            notes: scene.notes, // Pre-fill speaker notes with template guidance
            background: sceneBackground, // Use template backgroundImage if available
            layoutPreset: scene.layoutPreset, // Preserve template layout preset
          },
        };
      });

      // Create scenes and collect their actual IDs
      const createdSceneIds: number[] = [];
      for (const sceneData of sceneDataList) {
        const createdScene = await projectsManager.createScene(sceneData);
        createdSceneIds.push(createdScene.id);
      }

      // Create transitions using the actual scene IDs returned from database
      for (let i = 0; i < createdSceneIds.length - 1; i++) {
        const fromSceneId = createdSceneIds[i];
        const toSceneId = createdSceneIds[i + 1];
        
        await projectsManager.createTransition({
          projectId: newProject.id,
          fromSceneId,
          toSceneId,
          type: 'fade',
          duration: 1.0,
          sortOrder: i,
        });
      }

      setIsTemplateGalleryVisible(false);
      
      // Navigate to the new project
      navigate(`/recorder?mode=project&projectId=${newProject.id}`);
    } catch (error) {
      console.error('Failed to create project from template:', error);
      setCreatingProject(false);
      Modal.error({
        title: 'Failed to Create Project',
        content: 'There was an error creating your project from the template. Please try again.',
        okButtonProps: {
          style: { backgroundColor: '#4A3CFA', borderColor: '#4A3CFA' }
        }
      });
    }
  };

  const formatDuration = (seconds?: number) => {
    if (!seconds) return '0:00';
    const mins = Math.floor(seconds / 60);
    const secs = Math.floor(seconds % 60);
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    const now = new Date();
    const diffTime = Math.abs(now.getTime() - date.getTime());
    const diffDays = Math.floor(diffTime / (1000 * 60 * 60 * 24));
    
    if (diffDays === 0) return 'Today';
    if (diffDays === 1) return 'Yesterday';
    if (diffDays < 7) return `${diffDays} days ago`;
    if (diffDays < 30) return `${Math.floor(diffDays / 7)} weeks ago`;
    
    return date.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
    });
  };

  const getProgressPercent = (recorded: number, total: number) => {
    if (total === 0) return 0;
    return Math.round((recorded / total) * 100);
  };

  const getProgressStatus = (recorded: number, total: number) => {
    if (total === 0) return 'Get Started!';
    if (recorded === 0) return 'Ready To Record';
    if (recorded === total) return 'Complete';
    return `${recorded}/${total} Recorded`;
  };

  const getEstimatedDuration = (project: Project) => {
    const recordedCount = project.recordedSceneCount || 0;
    
    // If there are recorded scenes, show the recorded duration
    if (recordedCount > 0 && project.recordedDurationMs && project.recordedDurationMs > 0) {
      const durationInSeconds = Math.floor(project.recordedDurationMs / 1000);
      return formatDuration(durationInSeconds);
    }
    
    // If no recorded scenes, show the planned/estimated duration with ~ prefix
    if (project.plannedDurationMs && project.plannedDurationMs > 0) {
      const durationInSeconds = Math.floor(project.plannedDurationMs / 1000);
      return `~${formatDuration(durationInSeconds)}`;
    }
    
    // Fallback for legacy projects without duration data
    return '—';
  };

  return (
    <ConfigProvider
      theme={{
        token: {
          colorPrimary: '#4A3CFA',
          colorPrimaryHover: '#7B61FF',
          colorLink: '#4A3CFA',
          colorLinkHover: '#7B61FF',
          borderRadius: 8,
          fontSize: 14,
        },
        components: {
          Modal: {
            borderRadiusLG: 16,
            padding: 0,
          },
          Tabs: {
            colorPrimary: '#4A3CFA',
            itemActiveColor: '#4A3CFA',
            itemHoverColor: '#7B61FF',
            inkBarColor: '#4A3CFA',
          },
          Button: {
            colorPrimary: '#4A3CFA',
            colorPrimaryHover: '#7B61FF',
            colorPrimaryActive: '#3829C9',
            borderRadius: 6,
            controlHeight: 40,
            fontWeight: 500,
          },
          Card: {
            borderRadiusLG: 12,
          },
        },
      }}
    >
      <div className="projects-dashboard">
        {/* Onboarding Checklist - only shown until all tasks complete */}
        <OnboardingChecklist />
        
        {/* Quick Actions Row */}
        <div className="quick-actions-row">
          <QuickActionCard
            image="/quick-action-buttons/record-now.jpeg"
            icon={Zap}
            title="Record Now"
            description="Launch a fresh recording session instantly"
            onClick={handleStartBlank}
            iconColor="#F59E0B"
            iconBgColor="rgba(245, 158, 11, 0.08)"
          />
          <QuickActionCard
            image="/quick-action-buttons/browse-templates.jpeg"
            icon={Film}
            title="Browse Templates"
            description="Explore ready-made video project blueprints"
            onClick={() => setIsTemplateGalleryVisible(true)}
          />
          <QuickActionCard
            image="/images/import-presentation.png"
            icon={FileSliders}
            title="Import Presentation"
            description="Turn slides into video scenes with AI scripts"
            onClick={() => setShowPresentationImport(true)}
            iconColor="#2563EB"
            iconBgColor="rgba(37, 99, 235, 0.08)"
          />
          <QuickActionCard
            image="/quick-action-buttons/build-your-own.jpeg"
            icon={Plus}
            title="Build Your Own"
            description="Design a custom template from scratch"
            onClick={() => setShowTemplateCustomizer(true)}
            iconColor="#10B981"
            iconBgColor="rgba(16, 185, 129, 0.08)"
          />
        </div>

        {/* Explore Templates Carousel */}
        <TemplateCarousel
          onViewAll={() => setIsTemplateGalleryVisible(true)}
          onTemplateSelect={handleTemplateSelect}
          onPreview={(template) => setPreviewTemplate(template)}
          onMakeItYours={(template) => {
            initMakeItYours(template);
            setSelectedTemplateForWizard(template);
            setShowMakeItYoursWizard(true);
          }}
        />

        {/* My Recents Section */}
        <div className="my-recents-section">
          <div className="my-recents-header">
            <h3 className="my-recents-title">My Recents</h3>
            <Segmented
              value={viewMode}
              onChange={(value) => setViewMode(value as ViewMode)}
              options={[
                {
                  label: <LayoutGrid size={16} />,
                  value: 'grid',
                },
                {
                  label: <List size={16} />,
                  value: 'list',
                },
              ]}
              className="view-mode-switcher"
            />
          </div>

      {loading ? (
        viewMode === 'grid' ? (
          <Row gutter={[16, 16]} className="projects-grid">
            {[1, 2, 3, 4, 5, 6, 7, 8].map((i) => (
              <Col key={i} xs={24} sm={12} lg={8} xl={6}>
                <div className="project-card-skeleton">
                  <div className="skeleton-thumbnail" />
                  <div className="skeleton-content">
                    <div className="skeleton-title" />
                    <div className="skeleton-description" />
                    <div className="skeleton-description short" />
                    <div className="skeleton-meta">
                      <div className="skeleton-tag" />
                      <div className="skeleton-date" />
                    </div>
                  </div>
                </div>
              </Col>
            ))}
          </Row>
        ) : (
          <div className="projects-list">
            <div className="list-header">
              <div className="list-header-thumbnail">Preview</div>
              <div className="list-header-content">Project</div>
              <div className="list-header-meta">Ratio</div>
              <div className="list-header-duration">Duration</div>
              <div className="list-header-status">Status</div>
              <div className="list-header-date">Modified</div>
              <div className="list-header-actions"></div>
            </div>
            {[1, 2, 3, 4, 5, 6, 7, 8].map((i) => (
              <div key={i} className="project-list-item-skeleton">
                <div className="skeleton-list-thumbnail" />
                <div className="skeleton-list-content">
                  <div className="skeleton-list-title" />
                  <div className="skeleton-list-description" />
                </div>
                <div className="skeleton-list-meta" />
                <div className="skeleton-list-duration" />
                <div className="skeleton-list-status" />
                <div className="skeleton-list-date" />
                <div className="skeleton-list-actions" />
              </div>
            ))}
          </div>
        )
      ) : projects.length === 0 ? (
        <div className="empty-state">
          <Empty
            image={<img src="/images/empty-projects-icon.png" alt="No projects" style={{ width: 120, height: 120 }} />}
            description={
              <Space direction="vertical" size="small">
                <Text strong style={{ fontSize: 20 }}>Let's create something amazing!</Text>
                <Text type="secondary" style={{ fontSize: 14 }}>
                  Start recording with a template or jump right in with a blank canvas
                </Text>
              </Space>
            }
          >
            <Space size="large" style={{ marginTop: 16 }}>
              <Button
                type="primary"
                size="large"
                icon={<Film size={18} />}
                onClick={() => setIsTemplateGalleryVisible(true)}
                style={{ height: 40 }}
              >
                Browse Templates
              </Button>
              <Button
                size="large"
                icon={<Zap size={18} />}
                onClick={handleStartBlank}
                style={{ height: 40 }}
              >
                Quick Recording
              </Button>
            </Space>
          </Empty>
        </div>
      ) : viewMode === 'grid' ? (
        <Row gutter={[16, 16]} className="projects-grid">
          {projects.slice(0, displayCount).map((project) => {
            const recordedCount = project.recordedSceneCount || 0;
            const totalCount = project.sceneCount || 0;
            const ratio = getAspectRatioLabel(project.aspectRatio);

            return (
              <Col key={project.id} xs={24} sm={12} lg={8} xl={6}>
                <Card
                  hoverable
                  className="project-card"
                  onClick={() => handleOpenProject(project.id)}
                  cover={
                    project.firstSceneThumbnail ? (
                      <div className="project-thumbnail">
                        <img
                          alt={project.name}
                          src={project.firstSceneThumbnail}
                          style={{ width: '100%', height: 160, objectFit: 'cover' }}
                        />
                        <div className="duration-badge">
                          {getEstimatedDuration(project)}
                        </div>
                      </div>
                    ) : (
                      <div className="project-thumbnail-placeholder">
                        <Film size={48} color="#bfbfbf" />
                        <div className="duration-badge">
                          {getEstimatedDuration(project)}
                        </div>
                      </div>
                    )
                  }
                >
                  <div className="quick-actions-menu">
                    <Dropdown
                      menu={{
                        items: [
                          {
                            key: 'duplicate',
                            label: 'Duplicate Project',
                            icon: <Copy size={14} />,
                            onClick: (e) => handleDuplicateProject(project.id, e.domEvent),
                          },
                          {
                            key: 'delete',
                            label: 'Delete Project',
                            icon: <Trash2 size={14} />,
                            danger: true,
                            onClick: (e) => handleDeleteProject(project.id, e.domEvent),
                          },
                        ],
                      }}
                      trigger={['click']}
                    >
                      <Button
                        type="text"
                        icon={<MoreVertical size={16} />}
                        onClick={(e) => e.stopPropagation()}
                        size="small"
                        style={{ 
                          background: 'rgba(255, 255, 255, 0.95)', 
                          boxShadow: '0 2px 8px rgba(0,0,0,0.1)' 
                        }}
                      />
                    </Dropdown>
                  </div>

                  <div className="project-content">
                    <Title 
                      level={4} 
                      ellipsis={{ rows: 1, tooltip: project.name }} 
                      style={{ marginBottom: 4, fontSize: 16, lineHeight: 1.3 }}
                    >
                      {project.name}
                    </Title>

                    {project.description && (
                      <Paragraph
                        className="project-description"
                        ellipsis={{ rows: 2 }}
                      >
                        {project.description}
                      </Paragraph>
                    )}

                    {totalCount > 0 && (
                      <div className="progress-status">
                        {getProgressStatus(recordedCount, totalCount)}
                      </div>
                    )}

                    <div className="project-meta">
                      <span className="platform-tag">
                        {ratio.label}
                      </span>
                      {project.templateId === 'presentation-import' && (
                        <span className="platform-tag presentation-tag">
                          <Presentation size={12} />
                          Presentation
                        </span>
                      )}
                      <span className="meta-separator">•</span>
                      <span>{formatDate(project.updatedAt)}</span>
                    </div>
                  </div>
                </Card>
              </Col>
            );
          })}
        </Row>
      ) : (
        <div className="projects-list">
          <div className="list-header">
            <div className="list-header-thumbnail">Preview</div>
            <div className="list-header-content">Project</div>
            <div className="list-header-meta">Ratio</div>
            <div className="list-header-type">Type</div>
            <div className="list-header-duration">Duration</div>
            <div className="list-header-status">Status</div>
            <div className="list-header-date">Modified</div>
            <div className="list-header-actions"></div>
          </div>
          {projects.slice(0, displayCount).map((project) => {
            const recordedCount = project.recordedSceneCount || 0;
            const totalCount = project.sceneCount || 0;
            const ratio = getAspectRatioLabel(project.aspectRatio);

            return (
              <div
                key={project.id}
                className="project-list-item"
                onClick={() => handleOpenProject(project.id)}
              >
                <div className="list-item-thumbnail">
                  {project.firstSceneThumbnail ? (
                    <img
                      alt={project.name}
                      src={project.firstSceneThumbnail}
                    />
                  ) : (
                    <div className="thumbnail-placeholder">
                      <Film size={24} color="#bfbfbf" />
                    </div>
                  )}
                </div>

                <div className="list-item-content">
                  <div className="list-item-header">
                    <Title 
                      level={5} 
                      ellipsis={{ rows: 1, tooltip: project.name }} 
                      style={{ margin: 0, fontSize: 15 }}
                    >
                      {project.name}
                    </Title>
                    {project.description && (
                      <Text type="secondary" ellipsis style={{ fontSize: 13 }}>
                        {project.description}
                      </Text>
                    )}
                  </div>
                </div>

                <div className="list-item-meta">
                  <span className="platform-tag">
                    {ratio.label}
                  </span>
                </div>

                <div className="list-item-type">
                  {project.templateId === 'presentation-import' && (
                    <span className="platform-tag presentation-tag">
                      <Presentation size={12} />
                      Presentation
                    </span>
                  )}
                </div>

                <div className="list-item-duration">
                  <Text type="secondary" style={{ fontSize: 13 }}>
                    {getEstimatedDuration(project)}
                  </Text>
                </div>

                <div className="list-item-status">
                  {totalCount > 0 && (
                    <Text type="secondary" style={{ fontSize: 13 }}>
                      {getProgressStatus(recordedCount, totalCount)}
                    </Text>
                  )}
                </div>

                <div className="list-item-date">
                  <Text type="secondary" style={{ fontSize: 13 }}>
                    {formatDate(project.updatedAt)}
                  </Text>
                </div>

                <div className="list-item-actions">
                  <Dropdown
                    menu={{
                      items: [
                        {
                          key: 'duplicate',
                          label: 'Duplicate Project',
                          icon: <Copy size={14} />,
                          onClick: (e) => handleDuplicateProject(project.id, e.domEvent),
                        },
                        {
                          key: 'delete',
                          label: 'Delete Project',
                          icon: <Trash2 size={14} />,
                          danger: true,
                          onClick: (e) => handleDeleteProject(project.id, e.domEvent),
                        },
                      ],
                    }}
                    trigger={['click']}
                  >
                    <Button
                      type="text"
                      icon={<MoreVertical size={16} />}
                      onClick={(e) => e.stopPropagation()}
                      size="small"
                    />
                  </Dropdown>
                </div>
              </div>
            );
          })}
        </div>
      )}
      
      {displayCount < projects.length && (
        <div ref={loadMoreRef} className="load-more-trigger">
          {loadingMore && (
            viewMode === 'grid' ? (
              <Row gutter={[16, 16]} className="projects-grid" style={{ width: '100%' }}>
                {[1, 2, 3, 4].map((i) => (
                  <Col key={i} xs={24} sm={12} lg={8} xl={6}>
                    <div className="project-card-skeleton">
                      <div className="skeleton-thumbnail" />
                      <div className="skeleton-content">
                        <div className="skeleton-title" />
                        <div className="skeleton-description" />
                        <div className="skeleton-description short" />
                        <div className="skeleton-meta">
                          <div className="skeleton-tag" />
                          <div className="skeleton-date" />
                        </div>
                      </div>
                    </div>
                  </Col>
                ))}
              </Row>
            ) : (
              <div className="load-more-list-skeletons">
                {[1, 2, 3, 4].map((i) => (
                  <div key={i} className="project-list-item-skeleton">
                    <div className="skeleton-list-thumbnail" />
                    <div className="skeleton-list-content">
                      <div className="skeleton-list-title" />
                      <div className="skeleton-list-description" />
                    </div>
                    <div className="skeleton-list-meta" />
                    <div className="skeleton-list-duration" />
                    <div className="skeleton-list-status" />
                    <div className="skeleton-list-date" />
                    <div className="skeleton-list-actions" />
                  </div>
                ))}
              </div>
            )
          )}
        </div>
      )}
        </div> {/* End my-recents-section */}

      <Modal
        title="Create New Project"
        open={isModalVisible}
        onCancel={() => {
          setIsModalVisible(false);
          form.resetFields();
        }}
        footer={null}
        width={500}
      >
        <Form
          form={form}
          layout="vertical"
          onFinish={handleCreateProject}
          initialValues={{ aspectRatio: '16:9' }}
        >
          <Form.Item
            label="Project Name"
            name="name"
            rules={[{ required: true, message: 'Please enter a project name' }]}
          >
            <Input placeholder="e.g., Product Demo, Tutorial Video" size="large" />
          </Form.Item>

          <Form.Item
            label="Description (Optional)"
            name="description"
          >
            <Input.TextArea
              placeholder="Add a brief description of your project"
              rows={3}
              size="large"
            />
          </Form.Item>

          <Form.Item
            label="Aspect Ratio"
            name="aspectRatio"
            rules={[{ required: true, message: 'Please select an aspect ratio' }]}
          >
            <Select size="large">
              <Option value="16:9">16:9 - Landscape (YouTube, Vimeo)</Option>
              <Option value="9:16">9:16 - Portrait (Instagram, TikTok)</Option>
              <Option value="1:1">1:1 - Square (Instagram, LinkedIn)</Option>
              <Option value="4:5">4:5 - Portrait (Instagram Feed)</Option>
            </Select>
          </Form.Item>

          <Form.Item style={{ marginBottom: 0, marginTop: 24 }}>
            <Space style={{ width: '100%', justifyContent: 'flex-end' }}>
              <Button onClick={() => {
                setIsModalVisible(false);
                form.resetFields();
              }}>
                Cancel
              </Button>
              <Button type="primary" htmlType="submit" size="large">
                Create Project
              </Button>
            </Space>
          </Form.Item>
        </Form>
      </Modal>

      <Modal
        open={isTemplateGalleryVisible}
        onCancel={() => setIsTemplateGalleryVisible(false)}
        footer={null}
        width="min(1200px, 95vw)"
        className="template-gallery-modal"
        centered
        closable={true}
        styles={{
          body: { padding: 0 },
          header: { display: 'none' },
          mask: { 
            opacity: hideTemplateGalleryModal ? 0 : 1,
            visibility: hideTemplateGalleryModal ? 'hidden' : 'visible',
            transition: 'opacity 0.25s ease-in-out, visibility 0.25s ease-in-out',
          },
          content: { 
            opacity: hideTemplateGalleryModal ? 0 : 1,
            visibility: hideTemplateGalleryModal ? 'hidden' : 'visible',
            transform: hideTemplateGalleryModal ? 'scale(0.95)' : 'scale(1)',
            transition: 'opacity 0.25s ease-in-out, visibility 0.25s ease-in-out, transform 0.25s ease-in-out',
          },
        }}
      >
        <TemplateGallery 
          onTemplateSelect={handleTemplateSelect}
          onParentModalVisibility={(visible) => {
            setHideTemplateGalleryModal(!visible);
          }}
        />
      </Modal>

      <SmartTemplateWizard
        visible={showSmartWizard}
        onClose={() => setShowSmartWizard(false)}
        onSelectTemplate={() => {
          setShowSmartWizard(false);
          setIsTemplateGalleryVisible(true);
        }}
        onUseTemplate={handleTemplateSelect}
        onMakeItYours={() => {
          setShowSmartWizard(false);
          setShowMakeItYoursWizard(true);
        }}
      />

      <PresentationImportModal
        open={showPresentationImport}
        onClose={() => setShowPresentationImport(false)}
      />

      <MakeItYoursWizard
        visible={showMakeItYoursWizard}
        onClose={() => setShowMakeItYoursWizard(false)}
        onCreationStart={() => {
          setCreatingProject(true);
          setIsTemplateGalleryVisible(false);
        }}
        onComplete={(projectId) => {
          navigate(`/recorder?mode=project&projectId=${projectId}`);
        }}
      />

      <TemplateCustomizer
        visible={showTemplateCustomizer}
        onClose={() => setShowTemplateCustomizer(false)}
        mode="create"
        onSaveSuccess={() => {
          setShowTemplateCustomizer(false);
          loadProjects();
        }}
      />

      <TemplatePreviewModal
        template={previewTemplate}
        visible={!!previewTemplate}
        onClose={() => setPreviewTemplate(null)}
        onUseTemplate={(template) => {
          setPreviewTemplate(null);
          handleTemplateSelect(template);
        }}
        onMakeItYours={(template) => {
          setPreviewTemplate(null);
          initMakeItYours(template);
          setSelectedTemplateForWizard(template);
          setShowMakeItYoursWizard(true);
        }}
        onCustomizeTemplate={(template) => {
          setPreviewTemplate(null);
          setShowTemplateCustomizer(true);
        }}
      />

      <FeatureAnnouncementModal
        onExploreTemplates={() => setIsTemplateGalleryVisible(true)}
      />

      <ProjectCreationOverlay visible={creatingProject} />
    </div>
    </ConfigProvider>
  );
};
